import { useMemo, useState } from 'react';
import {
  Users,
  UserRound,
  Search as SearchIcon,
  Loader2,
  Linkedin,
  Mail,
  UserPlus,
  Pencil,
  Trash2,
  ExternalLink,
  Sparkles,
  CalendarClock,
  Check,
  Plus,
  Bookmark,
  MessageSquare,
  Phone,
  X,
  ListPlus,
  MapPin,
  GraduationCap,
  Building2,
  Globe,
  BadgeCheck,
} from 'lucide-react';
import { format } from 'date-fns';
import { it } from 'date-fns/locale';
import { useUiStore } from '../../store/ui';
import {
  useContacts,
  useContact,
  useContactTimeline,
  useCreateContact,
  useUpdateContact,
  useDeleteContact,
  type Contact,
  type TimelineEntry,
} from '../../hooks/useContacts';
import { useCreateFollowup, useUpdateFollowup, type Followup } from '../../hooks/useFollowups';
import { useExtTasks, useCreateExtTask } from '../../hooks/useExtTasks';
import { HelpHint } from '../ui/HelpHint';
import { Tooltip } from '../ui/Tooltip';

/**
 * Vista Contatti (CRM).
 * Colonna sinistra: elenco contatti con ricerca, avatar/iniziali, azienda e badge origine.
 * Colonna destra: scheda del contatto selezionato con dati, note editabili, riassunto AI,
 * follow-up e timeline unificata (mail ricevute + elementi salvati da LinkedIn).
 */
export function ContactsView() {
  const selectedContactId = useUiStore((s) => s.selectedContactId);
  const setSelectedContact = useUiStore((s) => s.setSelectedContact);
  const { data: contacts, isLoading } = useContacts();
  const [query, setQuery] = useState('');
  const [addOpen, setAddOpen] = useState(false);

  // Ricerca client-side su nome, azienda ed email (l'elenco e' gia' completo)
  const filtered = useMemo(() => {
    if (!contacts) return contacts;
    const q = query.trim().toLowerCase();
    if (!q) return contacts;
    return contacts.filter(
      (c) =>
        c.full_name?.toLowerCase().includes(q) ||
        c.company?.toLowerCase().includes(q) ||
        c.emails.some((e) => e.toLowerCase().includes(q)),
    );
  }, [contacts, query]);

  return (
    <div className="flex flex-1 overflow-hidden">

      {/* Colonna sinistra: elenco contatti */}
      <section className="flex w-[340px] shrink-0 flex-col border-r border-zinc-800/80 bg-zinc-950/20">
        <div className="flex h-10 items-center gap-2 border-b border-zinc-800/80 px-4 text-xs">
          <Users size={12} className="text-accent-400" />
          <span className="font-medium text-zinc-200">Contatti</span>
          {contacts && <span className="text-zinc-600">· {contacts.length}</span>}
          {/* Aiuto contestuale della sezione Contatti */}
          <HelpHint topic="contacts" />
          <button
            onClick={() => setAddOpen((v) => !v)}
            title="Nuovo contatto"
            className="ml-auto rounded-md p-1 text-zinc-400 transition hover:bg-zinc-800/60 hover:text-zinc-100"
          >
            <UserPlus size={14} />
          </button>
        </div>

        {/* Ricerca */}
        <div className="border-b border-zinc-800/80 p-2">
          <div className="relative">
            <SearchIcon size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-600" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Cerca per nome, azienda, email…"
              className="w-full rounded-md border border-zinc-800 bg-zinc-900/50 py-1.5 pl-8 pr-2 text-xs text-zinc-100 placeholder-zinc-500 outline-none transition focus:border-accent-500/50"
            />
          </div>
        </div>

        {addOpen && <NewContactForm onDone={() => setAddOpen(false)} />}

        <div className="flex-1 overflow-y-auto p-2">
          {isLoading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 size={16} className="animate-spin text-zinc-600" />
            </div>
          ) : !filtered || filtered.length === 0 ? (
            <ContactsEmptyState hasQuery={query.trim().length > 0} />
          ) : (
            <ul className="space-y-1">
              {filtered.map((c) => (
                <ContactRow
                  key={c.id}
                  contact={c}
                  active={selectedContactId === c.id}
                  onSelect={() => setSelectedContact(c.id)}
                />
              ))}
            </ul>
          )}
        </div>
      </section>

      {/* Colonna destra: scheda contatto */}
      {selectedContactId ? (
        <ContactDetailPanel contactId={selectedContactId} onDeleted={() => setSelectedContact(null)} />
      ) : (
        <div className="flex flex-1 flex-col items-center justify-center gap-2 text-zinc-600">
          <Users size={28} strokeWidth={1.5} />
          <p className="text-xs">Seleziona un contatto per vedere la scheda</p>
        </div>
      )}
    </div>
  );
}

// Icona origine contatto accanto al nome: LinkedIn / Email / inserito a mano.
// Tooltip con l'etichetta leggibile. Predisposto per la futura origine
// 'whatsapp' (es. { Icon: MessageCircle, label: 'WhatsApp', cls: 'text-emerald-400' }).
function SourceBadge({ source }: { source: string }) {
  const { Icon, label, cls } =
    source === 'linkedin'
      ? { Icon: Linkedin, label: 'LinkedIn', cls: 'text-sky-400' }
      : source === 'email'
        ? { Icon: Mail, label: 'Email', cls: 'text-amber-400' }
        : { Icon: UserRound, label: 'Manuale', cls: 'text-zinc-400' };
  return (
    <span
      title={`Origine: ${label}`}
      aria-label={`Origine: ${label}`}
      className={'inline-flex shrink-0 items-center ' + cls}
    >
      <Icon size={13} />
    </span>
  );
}

// Avatar del contatto: immagine se disponibile, altrimenti iniziali
function ContactAvatar({ contact, size = 'md' }: { contact: Contact; size?: 'md' | 'lg' }) {
  const initials = (contact.full_name ?? '?')
    .split(/\s+/)
    .map((p) => p[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase();
  const cls = size === 'lg' ? 'wm-crm-avatar is-lg' : 'wm-crm-avatar';
  return contact.avatar_url ? (
    <img src={contact.avatar_url} alt="" className={cls} />
  ) : (
    <span className={cls}>{initials || '?'}</span>
  );
}

function ContactRow({
  contact,
  active,
  onSelect,
}: {
  contact: Contact;
  active: boolean;
  onSelect: () => void;
}) {
  return (
    <li>
      <button
        onClick={onSelect}
        className={
          'flex w-full items-center gap-2.5 rounded-lg border p-2 text-left transition ' +
          (active
            ? 'border-accent-500/40 bg-accent-500/10'
            : 'border-transparent hover:border-zinc-800 hover:bg-zinc-900/50')
        }
      >
        <ContactAvatar contact={contact} />
        <div className="min-w-0 flex-1">
          <div className="truncate text-xs font-medium text-zinc-100">{contact.full_name}</div>
          <div className="truncate text-[11px] text-zinc-500">
            {contact.company ?? contact.headline ?? contact.emails[0] ?? '—'}
          </div>
        </div>
        <SourceBadge source={contact.source} />
      </button>
    </li>
  );
}

// Empty state dell'elenco: invita a installare l'estensione Chrome (CTA → tab Estensione)
function ContactsEmptyState({ hasQuery }: { hasQuery: boolean }) {
  const openSettings = useUiStore((s) => s.openSettings);
  if (hasQuery) {
    return (
      <div className="py-12 text-center text-xs text-zinc-500">Nessun contatto trovato.</div>
    );
  }
  return (
    <div className="flex flex-col items-center gap-3 rounded-lg border border-dashed border-zinc-800 px-4 py-10 text-center">
      <Linkedin size={22} className="text-zinc-600" strokeWidth={1.5} />
      <p className="text-xs text-zinc-400">
        Nessun contatto ancora.
        <br />
        Installa l&rsquo;estensione Chrome per iniziare a salvare contatti da LinkedIn.
      </p>
      <button
        onClick={() => openSettings('extension')}
        className="wm-btn-accent rounded-md px-3 py-1.5 text-[11px] font-medium transition"
      >
        Configura l&rsquo;estensione
      </button>
    </div>
  );
}

// Form inline per creare un contatto manuale (nome + azienda + email)
function NewContactForm({ onDone }: { onDone: () => void }) {
  const [name, setName] = useState('');
  const [company, setCompany] = useState('');
  const [email, setEmail] = useState('');
  const create = useCreateContact();

  const submit = async () => {
    if (!name.trim()) return;
    await create.mutateAsync({
      full_name: name.trim(),
      company: company.trim() || null,
      emails: email.trim() ? [email.trim()] : [],
      source: 'manual',
    });
    onDone();
  };

  const inputCls =
    'w-full rounded-md border border-zinc-800 bg-zinc-900/50 px-2 py-1.5 text-xs text-zinc-100 placeholder-zinc-500 outline-none transition focus:border-accent-500/50';

  return (
    <div className="space-y-2 border-b border-zinc-800/80 bg-zinc-900/30 p-3">
      <input autoFocus value={name} onChange={(e) => setName(e.target.value)} placeholder="Nome e cognome *" className={inputCls} />
      <input value={company} onChange={(e) => setCompany(e.target.value)} placeholder="Azienda" className={inputCls} />
      <input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Email" className={inputCls} />
      <div className="flex justify-end gap-2">
        <button onClick={onDone} className="rounded-md px-2.5 py-1 text-[11px] text-zinc-400 transition hover:bg-zinc-800/60 hover:text-zinc-200">
          Annulla
        </button>
        <button
          onClick={submit}
          disabled={!name.trim() || create.isPending}
          className="wm-btn-accent flex items-center gap-1 rounded-md px-2.5 py-1 text-[11px] font-medium transition disabled:opacity-50"
        >
          {create.isPending ? <Loader2 size={11} className="animate-spin" /> : <Plus size={11} />}
          Crea contatto
        </button>
      </div>
      {create.isError && <p className="text-[11px] text-rose-400">Errore durante la creazione del contatto.</p>}
    </div>
  );
}

// Bottone "Pianifica visita": accoda un task `visit_profile` nella coda
// copilota dell'estensione Chrome (l'utente aprirà e visiterà il profilo A
// MANO dal popup). Disabilitato se per questo contatto c'è già un task
// pending in coda, per evitare doppioni.
function PlanVisitButton({ contact }: { contact: Contact }) {
  const { data: tasks } = useExtTasks('pending');
  const createTask = useCreateExtTask();

  const alreadyQueued = !!tasks?.some((t) => t.contact_id === contact.id);

  const plan = async () => {
    if (!contact.linkedin_url || alreadyQueued) return;
    try {
      await createTask.mutateAsync({
        task_type: 'visit_profile',
        target_url: contact.linkedin_url,
        contact_id: contact.id,
      });
    } catch {
      /* esito mostrato dal label del bottone (isError) */
    }
  };

  return (
    <button
      onClick={plan}
      disabled={alreadyQueued || createTask.isPending}
      title={
        alreadyQueued
          ? 'Visita già in coda: la trovi nel popup dell’estensione'
          : 'Accoda una visita al profilo nell’estensione Chrome (la esegui tu)'
      }
      className={
        'flex items-center gap-1.5 rounded-md border px-2.5 py-1.5 text-[11px] font-medium transition disabled:cursor-not-allowed ' +
        (alreadyQueued
          ? 'border-emerald-900/60 bg-emerald-950/20 text-emerald-300'
          : 'border-zinc-800 bg-zinc-900/50 text-zinc-300 hover:border-zinc-700 hover:text-zinc-100 disabled:opacity-50')
      }
    >
      {createTask.isPending ? (
        <Loader2 size={12} className="animate-spin" />
      ) : alreadyQueued ? (
        <Check size={12} />
      ) : (
        <ListPlus size={12} />
      )}
      {alreadyQueued ? 'Visita in coda' : createTask.isError ? 'Errore, riprova' : 'Pianifica visita'}
    </button>
  );
}

// ---------- Scheda contatto (colonna destra) ----------

function ContactDetailPanel({ contactId, onDeleted }: { contactId: string; onDeleted: () => void }) {
  const { data: contact, isLoading } = useContact(contactId);
  const { data: timeline, isLoading: timelineLoading } = useContactTimeline(contactId);
  const deleteContact = useDeleteContact();

  if (isLoading || !contact) {
    return (
      <div className="flex flex-1 items-center justify-center">
        <Loader2 size={18} className="animate-spin text-zinc-600" />
      </div>
    );
  }

  const remove = async () => {
    if (!window.confirm(`Eliminare il contatto “${contact.full_name}”? Verranno rimossi anche follow-up ed elementi collegati.`)) return;
    await deleteContact.mutateAsync(contact.id);
    onDeleted();
  };

  return (
    <section className="flex flex-1 flex-col overflow-y-auto">
      {/* Intestazione scheda */}
      <div className="flex items-start gap-4 border-b border-zinc-800/80 p-5">
        <ContactAvatar contact={contact} size="lg" />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <h2 className="truncate text-base font-semibold text-zinc-100">{contact.full_name}</h2>
            <SourceBadge source={contact.source} />
          </div>
          {contact.headline && <p className="mt-0.5 truncate text-xs text-zinc-400">{contact.headline}</p>}
          <div className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-zinc-500">
            {contact.company && <span>{contact.company}</span>}
            {contact.emails.map((e) => (
              <a key={e} href={`mailto:${e}`} className="flex items-center gap-1 transition hover:text-accent-300">
                <Mail size={11} /> {e}
              </a>
            ))}
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-1.5">
          {contact.linkedin_url && (
            <>
              <PlanVisitButton contact={contact} />
              <a
                href={contact.linkedin_url}
                target="_blank"
                rel="noreferrer"
                title="Apri il profilo LinkedIn"
                className="flex items-center gap-1.5 rounded-md border border-zinc-800 bg-zinc-900/50 px-2.5 py-1.5 text-[11px] font-medium text-zinc-300 transition hover:border-zinc-700 hover:text-zinc-100"
              >
                <Linkedin size={12} /> LinkedIn <ExternalLink size={10} />
              </a>
            </>
          )}
          <button
            onClick={remove}
            disabled={deleteContact.isPending}
            title="Elimina contatto"
            className="flex items-center gap-1.5 rounded-md border border-zinc-800 bg-zinc-900/50 px-2.5 py-1.5 text-[11px] font-medium text-zinc-400 transition hover:border-rose-900 hover:text-rose-300 disabled:opacity-50"
          >
            {deleteContact.isPending ? <Loader2 size={12} className="animate-spin" /> : <Trash2 size={12} />}
            Elimina
          </button>
        </div>
      </div>

      <div className="grid flex-1 grid-cols-1 gap-5 p-5 xl:grid-cols-2">
        {/* Colonna sinistra della scheda: riassunto AI, note, follow-up */}
        <div className="space-y-5">
          {contact.ai_summary && (
            <div className="rounded-lg border border-accent-500/20 bg-accent-500/5 p-3">
              <div className="mb-1.5 flex items-center gap-1.5 text-[11px] font-medium text-accent-300">
                <Sparkles size={12} /> Riassunto AI
              </div>
              <p className="text-xs leading-relaxed text-zinc-300">{contact.ai_summary}</p>
            </div>
          )}

          {/* Dati profilo LinkedIn catturati dall'estensione (l'"img 2":
              azienda, formazione, località, follower, contatti…) */}
          <ProfileDetailsSection items={contact.saved_items} />

          <NotesEditor contactId={contact.id} initialNotes={contact.notes} />

          <FollowupsSection contactId={contact.id} followups={contact.followups} />

          <CommentedPostsSection items={contact.saved_items} />
        </div>

        {/* Colonna destra della scheda: timeline unificata */}
        <div>
          <h3 className="mb-3 flex items-center gap-1.5 text-xs font-semibold text-zinc-200">
            <CalendarClock size={13} className="text-accent-400" /> Timeline
          </h3>
          {timelineLoading ? (
            <div className="flex items-center gap-2 py-6 text-xs text-zinc-500">
              <Loader2 size={13} className="animate-spin" /> Caricamento timeline…
            </div>
          ) : !timeline || timeline.length === 0 ? (
            <div className="rounded-lg border border-dashed border-zinc-800 py-8 text-center text-xs text-zinc-500">
              Nessuna attività registrata per questo contatto.
            </div>
          ) : (
            <ul className="wm-crm-timeline">
              {timeline.map((entry, i) => (
                <TimelineRow key={`${entry.type}-${entry.id ?? i}`} entry={entry} linkedinUrl={contact.linkedin_url} />
              ))}
            </ul>
          )}
        </div>
      </div>
    </section>
  );
}

// Editor delle note del contatto (salvataggio esplicito via PATCH)
function NotesEditor({ contactId, initialNotes }: { contactId: string; initialNotes: string | null }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(initialNotes ?? '');
  const update = useUpdateContact();

  const save = async () => {
    await update.mutateAsync({ id: contactId, body: { notes: draft.trim() || null } });
    setEditing(false);
  };

  return (
    <div>
      <div className="mb-2 flex items-center justify-between">
        <h3 className="flex items-center gap-1.5 text-xs font-semibold text-zinc-200">
          <Pencil size={12} className="text-accent-400" /> Note
        </h3>
        {!editing && (
          <button
            onClick={() => {
              setDraft(initialNotes ?? '');
              setEditing(true);
            }}
            className="text-[11px] text-zinc-500 transition hover:text-accent-300"
          >
            Modifica
          </button>
        )}
      </div>
      {editing ? (
        <div className="space-y-2">
          <textarea
            autoFocus
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            rows={4}
            placeholder="Appunti sul contatto…"
            className="w-full resize-y rounded-md border border-zinc-800 bg-zinc-900/50 p-2 text-xs text-zinc-100 placeholder-zinc-500 outline-none transition focus:border-accent-500/50"
          />
          <div className="flex justify-end gap-2">
            <button onClick={() => setEditing(false)} className="rounded-md px-2.5 py-1 text-[11px] text-zinc-400 transition hover:bg-zinc-800/60 hover:text-zinc-200">
              Annulla
            </button>
            <button
              onClick={save}
              disabled={update.isPending}
              className="wm-btn-accent flex items-center gap-1 rounded-md px-2.5 py-1 text-[11px] font-medium transition disabled:opacity-50"
            >
              {update.isPending ? <Loader2 size={11} className="animate-spin" /> : <Check size={11} />}
              Salva note
            </button>
          </div>
        </div>
      ) : (
        <p className="whitespace-pre-wrap rounded-md border border-zinc-800/80 bg-zinc-900/30 p-2.5 text-xs leading-relaxed text-zinc-400">
          {initialNotes?.trim() || 'Nessuna nota. Clicca “Modifica” per aggiungerne.'}
        </p>
      )}
    </div>
  );
}

// Icona per canale follow-up
function ChannelIcon({ channel }: { channel: string }) {
  if (channel === 'linkedin') return <Linkedin size={11} />;
  if (channel === 'call') return <Phone size={11} />;
  if (channel === 'email') return <Mail size={11} />;
  return <MessageSquare size={11} />;
}

// Sezione follow-up del contatto: elenco + form di aggiunta + "marca fatto"
function FollowupsSection({ contactId, followups }: { contactId: string; followups: Followup[] }) {
  const [adding, setAdding] = useState(false);
  const [dueAt, setDueAt] = useState('');
  const [note, setNote] = useState('');
  const [channel, setChannel] = useState('email');
  const create = useCreateFollowup();
  const update = useUpdateFollowup();

  const submit = async () => {
    if (!dueAt) return;
    await create.mutateAsync({
      contact_id: contactId,
      due_at: new Date(dueAt).toISOString(),
      note: note.trim(),
      channel,
    });
    setAdding(false);
    setDueAt('');
    setNote('');
  };

  // Ordina: aperti prima (per scadenza), completati in coda
  const sorted = [...followups].sort((a, b) => {
    if (a.status !== b.status) return a.status === 'done' ? 1 : -1;
    return new Date(a.due_at).getTime() - new Date(b.due_at).getTime();
  });

  return (
    <div>
      <div className="mb-2 flex items-center justify-between">
        <h3 className="flex items-center gap-1.5 text-xs font-semibold text-zinc-200">
          <CalendarClock size={12} className="text-accent-400" /> Follow-up
        </h3>
        <button
          onClick={() => setAdding((v) => !v)}
          className="flex items-center gap-1 text-[11px] text-zinc-500 transition hover:text-accent-300"
        >
          {adding ? <X size={11} /> : <Plus size={11} />}
          {adding ? 'Annulla' : 'Aggiungi'}
        </button>
      </div>

      {adding && (
        <div className="mb-3 space-y-2 rounded-md border border-zinc-800 bg-zinc-900/30 p-2.5">
          <div className="flex gap-2">
            <input
              type="datetime-local"
              value={dueAt}
              onChange={(e) => setDueAt(e.target.value)}
              className="flex-1 rounded-md border border-zinc-800 bg-zinc-900/50 px-2 py-1.5 text-xs text-zinc-100 outline-none transition focus:border-accent-500/50"
            />
            <select
              value={channel}
              onChange={(e) => setChannel(e.target.value)}
              className="rounded-md border border-zinc-800 bg-zinc-900/50 px-2 py-1.5 text-xs text-zinc-100 outline-none transition focus:border-accent-500/50"
            >
              <option value="email">Email</option>
              <option value="linkedin">LinkedIn</option>
              <option value="call">Telefonata</option>
              <option value="other">Altro</option>
            </select>
          </div>
          <input
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Nota (es. proporre demo)…"
            className="w-full rounded-md border border-zinc-800 bg-zinc-900/50 px-2 py-1.5 text-xs text-zinc-100 placeholder-zinc-500 outline-none transition focus:border-accent-500/50"
          />
          <div className="flex justify-end">
            <button
              onClick={submit}
              disabled={!dueAt || create.isPending}
              className="wm-btn-accent flex items-center gap-1 rounded-md px-2.5 py-1 text-[11px] font-medium transition disabled:opacity-50"
            >
              {create.isPending ? <Loader2 size={11} className="animate-spin" /> : <Plus size={11} />}
              Crea follow-up
            </button>
          </div>
        </div>
      )}

      {sorted.length === 0 ? (
        <p className="rounded-md border border-dashed border-zinc-800 py-4 text-center text-[11px] text-zinc-500">
          Nessun follow-up pianificato.
        </p>
      ) : (
        <ul className="space-y-1.5">
          {sorted.map((f) => {
            const done = f.status === 'done';
            const overdue = !done && new Date(f.due_at).getTime() < Date.now();
            return (
              <li
                key={f.id}
                className={
                  'flex items-center gap-2.5 rounded-md border px-2.5 py-2 ' +
                  (done ? 'border-zinc-800/60 bg-zinc-900/20 opacity-60' : 'border-zinc-800 bg-zinc-900/40')
                }
              >
                <Tooltip label={done ? 'Riapri follow-up' : 'Marca come fatto'}>
                  <button
                    onClick={() => update.mutate({ id: f.id, body: { status: done ? 'pending' : 'done' } })}
                    title={done ? 'Riapri follow-up' : 'Marca come fatto'}
                    className={
                      'flex shrink-0 items-center justify-center rounded border transition ' +
                      (done
                        ? 'border-emerald-600 bg-emerald-600/20 text-emerald-400'
                        : 'border-zinc-600 text-transparent hover:border-emerald-500 hover:text-emerald-500/50')
                    }
                    style={{ width: 18, height: 18 }}
                  >
                    <Check size={12} />
                  </button>
                </Tooltip>
                <div className="min-w-0 flex-1">
                  <div className={'truncate text-xs ' + (done ? 'text-zinc-500 line-through' : 'text-zinc-200')}>
                    {f.note?.trim() || 'Follow-up'}
                  </div>
                  <div className={'flex items-center gap-1.5 text-[11px] ' + (overdue ? 'text-rose-400' : 'text-zinc-500')}>
                    <ChannelIcon channel={String(f.channel)} />
                    {format(new Date(f.due_at), "d MMM yyyy 'alle' HH:mm", { locale: it })}
                    {overdue && ' · in ritardo'}
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

// Riga della timeline unificata: icona diversa per mail vs elemento LinkedIn
function TimelineRow({ entry, linkedinUrl }: { entry: TimelineEntry; linkedinUrl: string | null }) {
  const isMessage = entry.type === 'message';
  const title = isMessage
    ? (entry.subject ?? entry.title)?.trim() || '(senza oggetto)'
    : entry.title?.trim() || `Elemento ${entry.kind ?? 'LinkedIn'}`;
  const body = isMessage ? entry.ai_summary ?? entry.snippet : entry.content ?? entry.snippet;
  const href = !isMessage ? entry.url ?? linkedinUrl ?? undefined : undefined;

  return (
    <li className="wm-crm-timeline-item">
      <span className={'wm-crm-timeline-dot ' + (isMessage ? 'is-mail' : 'is-linkedin')}>
        {isMessage ? <Mail size={10} /> : entry.kind === 'post' ? <Bookmark size={10} /> : <Linkedin size={10} />}
      </span>
      <div className="min-w-0 flex-1 pb-4">
        <div className="flex items-baseline justify-between gap-2">
          {href ? (
            <a href={href} target="_blank" rel="noreferrer" className="truncate text-xs font-medium text-zinc-200 transition hover:text-accent-300">
              {title}
            </a>
          ) : (
            <span className="truncate text-xs font-medium text-zinc-200">{title}</span>
          )}
          <span className="shrink-0 text-[10px] text-zinc-600">
            {entry.date ? format(new Date(entry.date), 'd MMM yyyy', { locale: it }) : ''}
          </span>
        </div>
        <div className="text-[11px] text-zinc-500">
          {isMessage
            ? entry.from_name || entry.from_email ? `Mail da ${entry.from_name ?? entry.from_email}` : 'Mail ricevuta'
            : `LinkedIn · ${labelForKind(entry.kind)}`}
        </div>
        {body && <p className="mt-1 line-clamp-2 text-[11px] leading-relaxed text-zinc-400">{body}</p>}
      </div>
    </li>
  );
}

// Etichetta italiana per il tipo di elemento salvato
function labelForKind(kind?: string) {
  switch (kind) {
    case 'post':
      return 'post';
    case 'profile':
      return 'profilo';
    case 'engagement':
      return 'engagement';
    case 'dm':
      return 'messaggio diretto';
    default:
      return 'elemento';
  }
}


// ---------- Dettagli profilo LinkedIn (dal raw del saved_item 'profile') ----------
// Mostra tutte le info visibili catturate dall'estensione dalla pagina profilo:
// azienda attuale, formazione, località, follower, collegamenti, badge verificato
// e — se il pannello era aperto al salvataggio — email/telefono/siti web.
function ProfileDetailsSection({ items }: { items: import('../../hooks/useSavedItems').SavedItem[] }) {
  const profile = items.find((i) => i.kind === 'profile' && i.raw && typeof i.raw === 'object');
  const d = (profile?.raw ?? null) as null | {
    company?: string;
    education?: string;
    location?: string;
    followers?: string;
    connections?: string;
    verified?: boolean;
    email?: string;
    phone?: string;
    websites?: string[];
    twitter?: string;
  };
  if (!d) return null;

  const rows: { icon: typeof MapPin; label: string; value: React.ReactNode }[] = [];
  if (d.company) rows.push({ icon: Building2, label: 'Azienda', value: d.company });
  if (d.education) rows.push({ icon: GraduationCap, label: 'Formazione', value: d.education });
  if (d.location) rows.push({ icon: MapPin, label: 'Località', value: d.location });
  if (d.followers) rows.push({ icon: Users, label: 'Follower', value: d.followers });
  if (d.connections) rows.push({ icon: Linkedin, label: 'Collegamenti', value: d.connections });
  if (d.email)
    rows.push({
      icon: Mail,
      label: 'Email',
      value: (
        <a href={`mailto:${d.email}`} className="transition hover:text-accent-300">
          {d.email}
        </a>
      ),
    });
  if (d.phone) rows.push({ icon: Phone, label: 'Telefono', value: d.phone });
  if (d.websites?.length)
    rows.push({
      icon: Globe,
      label: 'Sito web',
      value: (
        <span className="flex flex-col">
          {d.websites.map((w) => (
            <a key={w} href={w} target="_blank" rel="noreferrer" className="truncate transition hover:text-accent-300">
              {w.replace(/^https?:\/\//, '')}
            </a>
          ))}
        </span>
      ),
    });

  if (rows.length === 0 && !d.verified) return null;

  return (
    <div>
      <h3 className="mb-2 flex items-center gap-1.5 text-xs font-semibold text-zinc-200">
        <UserRound size={13} className="text-accent-400" /> Dati profilo
        {d.verified && (
          <span className="flex items-center gap-1 rounded bg-sky-950/50 px-1.5 py-0.5 text-[10px] font-medium text-sky-300">
            <BadgeCheck size={11} /> Verificato
          </span>
        )}
      </h3>
      <dl className="space-y-1.5 rounded-lg border border-zinc-800/80 bg-zinc-900/40 px-3 py-2.5">
        {rows.map(({ icon: Icon, label, value }) => (
          <div key={label} className="flex items-start gap-2 text-xs">
            <Icon size={13} className="mt-0.5 shrink-0 text-zinc-500" />
            <dt className="w-24 shrink-0 text-zinc-500">{label}</dt>
            <dd className="min-w-0 flex-1 break-words text-zinc-300">{value}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

// ---------- Post commentati dal contatto (engagement LinkedIn) ----------
// Elenca i post su cui questo profilo ha commentato (kind='engagement'),
// con link diretto al post su LinkedIn: la provenienza e' sempre raggiungibile.
function CommentedPostsSection({ items }: { items: import('../../hooks/useSavedItems').SavedItem[] }) {
  const commented = items.filter((i) => i.kind === 'engagement');
  if (commented.length === 0) return null;

  return (
    <div>
      <h3 className="mb-2 flex items-center gap-1.5 text-xs font-semibold text-zinc-200">
        <MessageSquare size={13} className="text-accent-400" /> Post commentati
        <span className="text-[10px] font-normal text-zinc-500">({commented.length})</span>
      </h3>
      <ul className="space-y-1.5">
        {commented.map((i) => (
          <li key={i.id} className="rounded-lg border border-zinc-800/80 bg-zinc-900/40 px-3 py-2">
            <div className="flex items-baseline justify-between gap-2">
              {i.url ? (
                <a
                  href={i.url}
                  target="_blank"
                  rel="noreferrer"
                  title="Apri il post su LinkedIn"
                  className="min-w-0 truncate text-xs font-medium text-zinc-200 transition hover:text-accent-300"
                >
                  {i.title?.trim() || 'Post LinkedIn'} <ExternalLink size={10} className="ml-0.5 inline" />
                </a>
              ) : (
                <span className="min-w-0 truncate text-xs font-medium text-zinc-400">
                  {i.title?.trim() || 'Post LinkedIn'}
                </span>
              )}
              <span className="shrink-0 text-[10px] text-zinc-600">
                {format(new Date(i.created_at), 'd MMM yyyy', { locale: it })}
              </span>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}
